import { CARD_BLANK_LOG_ROWS, CARD_LOG_ROWS, labelUrl, photoPath, type ItemCard } from "@iltocat/shared";
import { Barcode } from "./Barcode.js";
import { QrCode } from "./QrCode.js";

export type CardSize = "half" | "full";

/**
 * One item's card, printed at a fixed size on US Letter: the whole page, or the
 * top half of one (cut it, or fold it, or leave the bottom for the next card).
 * It is black on white through the label tokens, like the small label, and
 * keeps room to write on: ruled notes, and on the full page a movement log.
 *
 * The log starts with the stock history as it stands on the day it is
 * printed, and the rest of its rows are ruled and blank for the next hand.
 *
 * What it may say is decided in `buildItemCard`, not here.
 */
export function ItemCardSheet({
  card,
  itemId,
  coverPhotoId,
  origin,
  size,
  kind,
}: {
  card: ItemCard;
  itemId: string;
  coverPhotoId: string | null;
  origin: string;
  size: CardSize;
  kind: "stock" | "personal";
}) {
  const full = size === "full";
  const printed = new Date().toISOString().slice(0, 10);
  const blank = Math.max(CARD_BLANK_LOG_ROWS, CARD_LOG_ROWS - card.log.length);

  return (
    <article className={`item-card item-card-${size}`}>
      <header className="sheet-head">
        <div className="sheet-id">
          <p className="sheet-kicker">{kind === "stock" ? "Stock item card" : "Item card"}</p>
          <h2 className="sheet-title">{card.title}</h2>
          {card.serial && <p className="sheet-serial">S/N {card.serial}</p>}
        </div>
        <div className="sheet-qr">
          <QrCode text={labelUrl(origin, "item", itemId)} label={`Code for ${card.title}`} />
        </div>
      </header>

      {card.serial && (
        <div className="sheet-barcode">
          <Barcode text={card.serial} label={`Barcode for serial number ${card.serial}`} />
        </div>
      )}

      <dl className="sheet-facts">
        {card.facts.map((fact) => (
          <div key={fact.label} className="sheet-fact">
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>

      {full && (card.description || coverPhotoId) && (
        <section className="sheet-body">
          {coverPhotoId && <img className="sheet-photo" src={photoPath(coverPhotoId)} alt="" />}
          {card.description && <p className="sheet-description">{card.description}</p>}
        </section>
      )}

      {full && card.specs.length > 0 && (
        <section className="sheet-section">
          <h3 className="sheet-heading">Specifications</h3>
          <dl className="sheet-specs">
            {card.specs.map((spec, index) => (
              <div key={`${spec.label}-${index}`} className="sheet-fact">
                <dt>{spec.label}</dt>
                <dd>{spec.value}</dd>
              </div>
            ))}
          </dl>
          {card.moreSpecs > 0 && <p className="sheet-more">and {card.moreSpecs} more on the item screen</p>}
        </section>
      )}

      {full && (
        <section className="sheet-section">
          <h3 className="sheet-heading">Movement log</h3>
          <table className="sheet-log">
            <thead>
              <tr>
                <th>Date</th>
                <th>Moved to / from</th>
                <th>Qty</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {card.moreLog > 0 && (
                <tr className="sheet-log-more">
                  <td colSpan={4}>{card.moreLog} earlier on the item screen</td>
                </tr>
              )}
              {card.log.map((line, index) => (
                <tr key={`logged-${index}`}>
                  <td>{line.date}</td>
                  <td>{line.what}</td>
                  <td>{line.quantity}</td>
                  <td>{line.by}</td>
                </tr>
              ))}
              {Array.from({ length: blank }, (_, row) => (
                <tr key={row}>
                  <td />
                  <td />
                  <td />
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="sheet-section sheet-notes">
        <h3 className="sheet-heading">Notes</h3>
        <div className="sheet-lines" />
      </section>

      <footer className="sheet-foot">
        <span>Scan the code to open this item in utucat.</span>
        <span>Printed {printed}</span>
      </footer>
    </article>
  );
}
